// /account — My account (L05)
//
// Every signed-in person — CASHIER to SUPER_ADMIN — changes their own password
// and signs out of all devices here. Opens from "My account" in the user menu
// (D-E1). The current password is asked once you have one; the server keeps
// this browser signed in and signs out every other session (G-E2). Admin holds
// no offline bills, so "Sign out everywhere" needs no T17 guard here.

import { useState, type FormEvent } from 'react';
import { PageContainer, Badge, Button, Input, useToast } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { authService } from '@/services/auth.service';
import { SignOutAllDevicesCard } from '@/components/features/settings/SignOutAllDevicesCard';
import { GLASS_FORM, cn } from '@/constants/design-tokens';

function LockIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}

function PasswordCard({ hasPassword }: { hasPassword: boolean }) {
  const { toast } = useToast();
  const fetchProfile = useAuthStore((s) => s.fetchProfile);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const canSave = !saving && next.length > 0 && next === confirm && (!hasPassword || current.length > 0);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await authService.changePassword(
        hasPassword ? { password: next, current_password: current } : { password: next },
      );
      setCurrent('');
      setNext('');
      setConfirm('');
      toast({ title: 'Password changed. Other devices are signed out.', variant: 'success' });
      await fetchProfile().catch(() => undefined);
    } catch (err) {
      // The server's words for every refusal (password rule, wrong current
      // password, too many tries) — no copy of the rule in the client.
      setError(err instanceof Error ? err.message : 'Could not change your password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn(GLASS_FORM, 'p-5 animate-slide-up')}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white flex-shrink-0 shadow-sm">
          <LockIcon />
        </div>
        <h2 className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
          {hasPassword ? 'Change password' : 'Set a password'}
        </h2>
      </div>
      {!hasPassword && (
        <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
          You sign in with an email code. A password lets you sign in on the kitchen screen and the phone apps too.
        </p>
      )}
      <form onSubmit={onSubmit} className="space-y-3 max-w-md">
        {hasPassword && (
          <Input
            type="password"
            label="Current password"
            name="current_password"
            value={current}
            onChange={setCurrent}
            disabled={saving}
          />
        )}
        <Input type="password" label="New password" name="new_password" value={next} onChange={setNext} disabled={saving} />
        <Input
          type="password"
          label="Confirm new password"
          name="confirm_password"
          value={confirm}
          onChange={setConfirm}
          error={mismatch ? 'The two new passwords are not the same.' : undefined}
          disabled={saving}
        />
        {error && (
          <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
            {error}
          </p>
        )}
        <Button type="submit" disabled={!canSave} loading={saving}>
          {hasPassword ? 'Change password' : 'Set password'}
        </Button>
      </form>
    </div>
  );
}

export function MyAccountPage() {
  const profile = useAuthStore((s) => s.profile);

  return (
    <PageContainer>
      <div className="bg-gradient-to-r from-brand-600 to-indigo-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-white">My account</h1>
        <p className="mt-1 text-sm text-white/85">Change your password and sign out of other devices.</p>
      </div>

      <div className="space-y-6 max-w-3xl">
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-5 border border-neutral-100 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
              {(profile?.name ?? profile?.email ?? '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-neutral-900 dark:text-white truncate">{profile?.name ?? '—'}</p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{profile?.email}</p>
              {profile?.tenant?.business_name && (
                <p className="text-xs text-neutral-400 dark:text-neutral-500 truncate">{profile.tenant.business_name}</p>
              )}
            </div>
            {profile?.role && <Badge variant="info">{profile.role}</Badge>}
          </div>
        </div>

        <PasswordCard hasPassword={profile?.has_password === true} />
        <SignOutAllDevicesCard />
      </div>
    </PageContainer>
  );
}
